import React from "react";
import { Check, CreditCard } from "lucide-react";

/* ================= PLANS ================= */

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "/mo",
    description: "Best to explore Teqa basics",
    features: [
      "200 orders / month",
      "1 store",
      "1 team member",
      "Basic analytics (limited)",
      "Email support (48–72h)",
      "2 free integrations",
    ],
    highlighted: false,
  },
  {
    name: "Starter",
    price: "$19",
    period: "/mo",
    description: "For small growing stores",
    features: [
      "500 orders / month",
      "2 stores",
      "2 team members",
      "Shipping & COD overview",
      "Unlimited integrations",
    ],
    highlighted: true,
  },
  {
    name: "Advanced",
    price: "$245",
    period: "/mo",
    description: "Advanced analytics & performance",
    features: [
      "1,500 orders / month",
      "5 stores",
      "Carrier comparison",
      "Chat support",
      "Unlimited integrations",
    ],
    highlighted: false,
  },
  {
    name: "Professional",
    price: "$680",
    period: "/mo",
    description: "High-volume operations",
    features: [
      "5,000 orders / month",
      "10 stores",
      "Account manager",
      "Priority support",
      "Unlimited integrations",
    ],
    highlighted: true,
  },
];

/* ================= MOCK PAYMENT HISTORY ================= */

const payments = [
  { id: "INV-001", date: "01 Jun 2025", amount: "$680", status: "Paid" },
  { id: "INV-002", date: "01 May 2025", amount: "$680", status: "Paid" },
  { id: "INV-003", date: "01 Apr 2025", amount: "$680", status: "Paid" },
];

const SubscriptionList = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-12">

        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-slate-100">
            Subscription Plans
          </h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            Choose the best plan for your business
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`p-6 rounded-2xl border shadow-sm ${
                plan.highlighted
                  ? "border-indigo-600 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10"
                  : "border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900"
              }`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {plan.name}
              </h3>
              <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                {plan.description}
              </p>

              <div className="mt-4">
                <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">
                  {plan.price}
                </span>
                <span className="text-gray-500 dark:text-slate-400">
                  {plan.period}
                </span>
              </div>

              <ul className="mt-6 space-y-2 text-sm text-gray-600 dark:text-slate-300">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check size={14} className="text-green-600 dark:text-emerald-400" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button className="mt-6 w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                Upgrade
              </button>
            </div>
          ))}
        </div>

        {/* Payment History */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              Payment History
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800 border-b dark:border-slate-800">
                <tr>
                  <th className="px-6 py-3 text-left text-gray-600 dark:text-slate-400">Invoice</th>
                  <th className="px-6 py-3 text-left text-gray-600 dark:text-slate-400">Date</th>
                  <th className="px-6 py-3 text-left text-gray-600 dark:text-slate-400">Amount</th>
                  <th className="px-6 py-3 text-left text-gray-600 dark:text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((pay, index) => (
                  <tr key={index} className="border-b dark:border-slate-800 text-gray-700 dark:text-slate-300">
                    <td className="px-6 py-4">{pay.id}</td>
                    <td className="px-6 py-4">{pay.date}</td>
                    <td className="px-6 py-4">{pay.amount}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 rounded-full text-xs">
                        {pay.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>

      </div>
    </div>
  );
};

export default SubscriptionList;